import React, { useState, useEffect } from 'react';
import { format, addDays, isToday, isTomorrow, startOfDay } from 'date-fns';
import { Send, Calendar as CalendarIcon, FileText, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';

interface PublishEpisodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublish: (mode: 'publish' | 'schedule' | 'draft', scheduledDateTime?: Date) => void;
  isLoading?: boolean;
  hasDescription: boolean;
  /** Time presets from company settings (hours in 24hr format, 0-23). Falls back to defaults if not provided. */
  timePresets?: number[];
}

type PublishMode = 'publish' | 'schedule' | 'draft';

// Default time presets (hours in 24hr format)
const DEFAULT_TIME_PRESETS = [6, 7, 8, 9, 12];

// Format hour (24hr) to display string (e.g., 6 -> "6:00 AM", 14 -> "2:00 PM")
const formatHourToDisplay = (hour: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
};

function formatDateLabel(date: Date): string {
  const dayOfWeek = format(date, 'EEE'); // Mon, Tue, Wed, etc.
  if (isToday(date)) return `Today (${dayOfWeek})`;
  if (isTomorrow(date)) return `Tomorrow (${dayOfWeek})`;
  return format(date, 'EEE, MMM d'); // Wed, Jan 15
}

function parseTimeToDate(date: Date, timeString: string): Date {
  const match = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return date;

  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  // Build the date explicitly from components to avoid any timezone edge cases
  // This ensures we get the correct local date with the selected time
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create a new Date using the date components and selected time
  // This explicitly uses local timezone interpretation
  return new Date(year, month, day, hour, minute, 0, 0);
}

function formatCustomTime(hour: string, minute: string, amPm: string): string {
  return `${hour}:${minute} ${amPm}`;
}

export function PublishEpisodeDialog({
  open,
  onOpenChange,
  onPublish,
  isLoading = false,
  hasDescription,
  timePresets,
}: PublishEpisodeDialogProps) {
  // Use provided time presets or fall back to defaults
  const effectiveTimePresets = timePresets?.length ? timePresets : DEFAULT_TIME_PRESETS;
  // Convert hour presets to display strings for UI
  const TIME_PRESETS_DISPLAY = effectiveTimePresets.map(formatHourToDisplay);
  // Default selected time is the first preset or 8:00 AM
  const defaultTime = TIME_PRESETS_DISPLAY[0] || '8:00 AM';

  const [mode, setMode] = useState<PublishMode>('schedule');
  const [selectedDate, setSelectedDate] = useState<Date>(addDays(new Date(), 1));
  const [selectedTime, setSelectedTime] = useState(defaultTime);
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customHour, setCustomHour] = useState('8');
  const [customMinute, setCustomMinute] = useState('00');
  const [customAmPm, setCustomAmPm] = useState<'AM' | 'PM'>('AM');
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setMode('schedule');
      setSelectedDate(addDays(new Date(), 1));
      setSelectedTime(defaultTime);
      setIsCustomTime(false);
      setCustomHour('8');
      setCustomMinute('00');
      setCustomAmPm('AM');
    }
  }, [open, defaultTime]);

  const handleTimePresetClick = (time: string) => {
    setSelectedTime(time);
    setIsCustomTime(false);
  };

  const handleCustomClick = () => {
    setIsCustomTime(true);
    setSelectedTime(formatCustomTime(customHour, customMinute, customAmPm));
  };

  useEffect(() => {
    if (isCustomTime) {
      setSelectedTime(formatCustomTime(customHour, customMinute, customAmPm));
    }
  }, [customHour, customMinute, customAmPm, isCustomTime]);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const handleQuickDate = (type: 'today' | 'tomorrow') => {
    const date = type === 'today' ? new Date() : addDays(new Date(), 1);
    setSelectedDate(startOfDay(date));
    setCalendarOpen(false);
  };

  const handleSubmit = () => {
    if (mode === 'schedule') {
      const scheduledDateTime = parseTimeToDate(selectedDate, selectedTime);
      onPublish('schedule', scheduledDateTime);
    } else {
      onPublish(mode);
    }
    onOpenChange(false);
  };

  const getButtonLabel = () => {
    if (isLoading) return 'Publishing...';
    switch (mode) {
      case 'publish':
        return 'Publish';
      case 'schedule':
        return 'Schedule';
      case 'draft':
        return 'Save as Draft';
    }
  };

  const isSubmitDisabled = isLoading || (mode === 'schedule' && !selectedDate);

  // Tab button styles with glow effect
  const getTabStyle = (tabMode: PublishMode) => {
    const isSelected = mode === tabMode;
    return {
      boxShadow: isSelected ? `0 0 12px var(--pm-cyan-glow), inset 0 1px 0 var(--pm-surface-4)` : 'none',
      borderColor: isSelected ? 'var(--pm-green)' : undefined,
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[550px] bg-podcast border-border p-6">
        <DialogHeader>
          <DialogTitle>Publish Episode</DialogTitle>
          {hasDescription ? (
            <DialogDescription>
              Choose how you want to publish this episode to Podbean.
            </DialogDescription>
          ) : (
            <DialogDescription className="text-amber-500 dark:text-amber-400">
              A description is required before publishing.
            </DialogDescription>
          )}
        </DialogHeader>

        {hasDescription ? (
          <>
            {/* Horizontal Tab Buttons */}
            <div className="grid grid-cols-3 gap-3 mt-2">
              <button
                onClick={() => setMode('publish')}
                style={getTabStyle('publish')}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                  mode === 'publish'
                    ? 'bg-primary/10'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <Send className="h-5 w-5" />
                <span className="text-sm font-medium">Publish Now</span>
              </button>
              <button
                onClick={() => setMode('schedule')}
                style={getTabStyle('schedule')}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                  mode === 'schedule'
                    ? 'bg-primary/10'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <CalendarIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Schedule</span>
              </button>
              <button
                onClick={() => setMode('draft')}
                style={getTabStyle('draft')}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all duration-200 ${
                  mode === 'draft'
                    ? 'bg-primary/10'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span className="text-sm font-medium">Save Draft</span>
              </button>
            </div>

            {/* Fixed height content area - prevents dialog resize when switching tabs */}
            <div className="h-[240px] flex flex-col mt-6">
              {/* Publish Now Confirmation */}
              {mode === 'publish' && (
                <div className="flex-1 flex items-center justify-center">
                  <div
                    className="flex flex-col items-center text-center p-6 rounded-xl border border-green-500/20 bg-green-500/5"
                    style={{
                      boxShadow: '0 0 30px var(--pm-green-bg), 0 0 60px var(--pm-green-bg)',
                    }}
                  >
                    <div
                      className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4"
                      style={{
                        boxShadow: '0 0 20px var(--pm-green-bg)',
                      }}
                    >
                      <Send className="h-7 w-7 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Ready to go live</h3>
                    <p className="text-sm text-muted-foreground max-w-[260px]">
                      Your episode will be available on Podbean immediately
                    </p>
                  </div>
                </div>
              )}

              {/* Save Draft Confirmation */}
              {mode === 'draft' && (
                <div className="flex-1 flex items-center justify-center">
                  <div
                    className="flex flex-col items-center text-center p-6 rounded-xl border border-[var(--pm-border-muted)] bg-[var(--pm-surface-2)]"
                    style={{
                      boxShadow: '0 0 30px var(--pm-surface-3), 0 0 60px var(--pm-surface-2)',
                    }}
                  >
                    <div
                      className="w-16 h-16 rounded-full bg-[var(--pm-surface-4)] flex items-center justify-center mb-4"
                      style={{
                        boxShadow: '0 0 20px var(--pm-surface-4)',
                      }}
                    >
                      <FileText className="h-7 w-7 text-[var(--pm-text-faint)]" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Save for later</h3>
                    <p className="text-sm text-muted-foreground max-w-[260px]">
                      Your episode will be stored on Podbean but won't be visible to listeners
                    </p>
                  </div>
                </div>
              )}

              {/* Schedule Panel */}
              {mode === 'schedule' && (
                <div
                  className="space-y-5 p-5 rounded-xl border border-primary/20 bg-primary/5"
                  style={{
                    boxShadow: `0 0 20px var(--pm-green-bg), inset 0 1px 0 var(--pm-surface-2)`,
                  }}
                >
                  {/* Date and Time Sentence */}
                  <div className="flex items-center gap-2 text-sm pb-1">
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <button className="flex items-center gap-1 font-semibold text-foreground hover:text-primary transition-colors">
                          {formatDateLabel(selectedDate)}
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[200]" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          disabled={(date) => date < startOfDay(new Date())}
                          className="rounded-lg"
                        />
                        <div className="flex gap-2 p-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleQuickDate('today')}
                          >
                            Today
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleQuickDate('tomorrow')}
                          >
                            Tomorrow
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <span className="text-muted-foreground">at</span>
                    <span className="font-semibold text-foreground">{selectedTime}</span>
                  </div>

                  {/* Time Preset Chips */}
                  <div className="flex flex-wrap gap-2.5">
                    {TIME_PRESETS_DISPLAY.map((time) => {
                      const isSelected = selectedTime === time && !isCustomTime;
                      return (
                        <button
                          key={time}
                          onClick={() => handleTimePresetClick(time)}
                          style={{
                            boxShadow: isSelected ? '0 0 10px var(--pm-cyan-glow)' : 'none',
                          }}
                          className={`px-3.5 py-2 text-sm rounded-lg border-2 transition-all duration-200 ${
                            isSelected
                              ? 'border-primary bg-primary/15 text-primary font-medium'
                              : 'border-border hover:border-muted-foreground/50'
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                    <button
                      onClick={handleCustomClick}
                      style={{
                        boxShadow: isCustomTime ? '0 0 10px var(--pm-cyan-glow)' : 'none',
                      }}
                      className={`px-3.5 py-2 text-sm rounded-lg border-2 transition-all duration-200 ${
                        isCustomTime
                          ? 'border-primary bg-primary/15 text-primary font-medium'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      Custom
                    </button>
                  </div>

                  {/* Custom Time Dropdowns - always reserve space */}
                  <div className={`flex items-center gap-2.5 ${isCustomTime ? 'visible' : 'invisible'}`}>
                    <Select value={customHour} onValueChange={setCustomHour}>
                      <SelectTrigger className="w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                          <SelectItem key={h} value={h.toString()}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground font-medium">:</span>
                    <Select value={customMinute} onValueChange={setCustomMinute}>
                      <SelectTrigger className="w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['00', '15', '30', '45'].map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={customAmPm} onValueChange={(v) => setCustomAmPm(v as 'AM' | 'PM')}>
                      <SelectTrigger className="w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">AM</SelectItem>
                        <SelectItem value="PM">PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                className="rounded-lg"
                style={{
                  boxShadow: isSubmitDisabled ? 'none' : '0 0 16px var(--pm-cyan-glow)',
                }}
              >
                {getButtonLabel()}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Please add an episode description before publishing. Click the title or description area to edit.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
